import React, { useEffect, useState } from 'react';

interface ContactMessage {
  Email: string;
  Subject: string;
  Message: string;
}

const getRandomNumber = () => Math.floor(Math.random() * 15);

const Contact: React.FC = () => {
  const [contactMessage, setContactMessage] = useState<ContactMessage>({
    Email: '',
    Subject: '',
    Message: '',
  });
  const [messageSent, setMessageSent] = useState(false);
  const [validationFail, setValidationFail] = useState(false);
  const [sending, setSending] = useState(false);
  const [mailStatus, setMailStatus] = useState('');
  const [valueA, setValueA] = useState(0);
  const [valueB, setValueB] = useState(0);
  const [robotValidation, setRobotValidation] = useState('');

  useEffect(() => {
    setValueA(getRandomNumber());
    setValueB(getRandomNumber());
  }, []);

  const handleValidSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseInt(robotValidation);
    if (!isNaN(parsed) && valueA + valueB === parsed) {
      setSending(true);
      try {
        const result = await httpClient(contactMessage);
        if (result !== undefined) {
          setMailStatus('Message sent — thanks for reaching out!');
          setMessageSent(true);
        } else {
          setMailStatus('Something went wrong. Please try again.');
        }
      } finally {
        setSending(false);
        setValueA(getRandomNumber());
        setValueB(getRandomNumber());
        setRobotValidation('');
      }
    } else {
      setValueA(getRandomNumber());
      setValueB(getRandomNumber());
      setRobotValidation('');
      setValidationFail(true);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactMessage(prev => ({ ...prev, [name]: value }));
  };

  const inputClass =
    'w-full bg-neutral-800 border border-neutral-700 focus:border-red-500 focus:outline-none text-white placeholder-gray-500 px-4 py-3 text-sm font-mono transition-colors duration-200';

  const renderForm = () => (
    <form onSubmit={handleValidSubmit} className="space-y-5">
      <div>
        <label className="block text-gray-400 font-mono text-xs uppercase tracking-widest mb-2">
          Email
        </label>
        <input
          className={inputClass}
          placeholder="your@email.com"
          name="Email"
          value={contactMessage.Email}
          type="email"
          autoComplete="email"
          maxLength={254}
          onChange={handleChange}
          required
        />
      </div>

      <div>
        <label className="block text-gray-400 font-mono text-xs uppercase tracking-widest mb-2">
          Subject
        </label>
        <input
          className={inputClass}
          placeholder="What is this about?"
          name="Subject"
          value={contactMessage.Subject}
          type="text"
          autoComplete="off"
          maxLength={200}
          minLength={2}
          onChange={handleChange}
          required
        />
      </div>

      <div>
        <label className="block text-gray-400 font-mono text-xs uppercase tracking-widest mb-2">
          Message
        </label>
        <textarea
          className={`${inputClass} resize-none h-36`}
          placeholder="Your message here..."
          name="Message"
          value={contactMessage.Message}
          maxLength={5000}
          minLength={10}
          onChange={handleChange}
          required
        />
      </div>

      <div>
        <label className="block text-gray-400 font-mono text-xs uppercase tracking-widest mb-2">
          Anti-spam: what is {valueA} + {valueB}?
        </label>
        <input
          className={`${inputClass} w-32`}
          type="number"
          value={robotValidation}
          onChange={e => setRobotValidation(e.target.value)}
          placeholder="?"
          required
        />
        {validationFail && (
          <p className="text-red-400 font-mono text-xs mt-2">Incorrect answer — are you a robot?</p>
        )}
      </div>

      <button
        type="submit"
        className="bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-mono text-xs uppercase tracking-widest px-8 py-3.5 transition-colors duration-200 disabled:opacity-50"
        disabled={sending}
      >
        Send Message
      </button>
    </form>
  );

  return (
    <section id="contact" className="bg-neutral-900 py-24 px-6">
      <div className="max-w-2xl mx-auto">
        <p className="text-red-500 font-mono text-xs uppercase tracking-widest text-center mb-3">
          Let's talk
        </p>
        <h2 className="text-4xl font-bold text-white text-center mb-4">Get in Touch</h2>
        <p className="text-gray-400 text-center mb-12 text-sm">
          Whether it's a recording project or a software challenge — I'd love to hear from you.
        </p>

        {!messageSent && !sending && renderForm()}

        {sending && (
          <div className="text-center py-12">
            <p className="text-gray-400 font-mono text-sm animate-pulse">Sending your message...</p>
          </div>
        )}

        {messageSent && mailStatus && (
          <div className="text-center py-12 border border-neutral-800 bg-black/40">
            <svg className="w-10 h-10 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-white font-mono text-sm">{mailStatus}</p>
          </div>
        )}

        {!messageSent && mailStatus && (
          <p className="text-red-400 font-mono text-sm text-center mt-4">{mailStatus}</p>
        )}
      </div>
    </section>
  );
};

export default Contact;

async function httpClient(contactMessage: ContactMessage) {
  try {
    const response = await fetch('/api/Contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: contactMessage.Email,
        subject: contactMessage.Subject,
        message: contactMessage.Message,
      }),
    });
    return await response.text();
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
}
